import { Calendar, MapPin, Pencil } from "lucide-react"
import { useMemo, useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import type {
  ContentStatus,
  PaginationMeta,
  StudioEventRow,
  StudioPoiRow,
  StudioUrls,
} from "@/lib/types"

import { EventSheet } from "./EventSheet"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ScopeFilter = "upcoming" | "past"

export interface EventsTabProps {
  events: { records: StudioEventRow[]; meta: PaginationMeta }
  pois: { records: StudioPoiRow[]; meta: PaginationMeta }
  urls: StudioUrls
  editingEvent: StudioEventRow | null | "new"
  onEditingEventChange: (event: StudioEventRow | null | "new") => void
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusBadgeVariant(status: ContentStatus) {
  switch (status) {
    case "approved":
      return "default" as const
    case "pending":
    case "draft":
      return "secondary" as const
    case "needs_changes":
      return "outline" as const
    case "rejected":
    case "archived":
      return "destructive" as const
    default:
      return "secondary" as const
  }
}

function recurrenceLabel(recurrence: { frequency?: string } | null): string | null {
  if (!recurrence) return null
  const freq = recurrence.frequency
  if (!freq) return null
  // Capitalise first letter
  return freq.charAt(0).toUpperCase() + freq.slice(1).toLowerCase()
}

function monthKey(iso: string): string {
  return new Intl.DateTimeFormat("en", { month: "long", year: "numeric" }).format(
    new Date(iso),
  )
}

function dayNumber(iso: string): string {
  return new Date(iso).getDate().toString()
}

function shortMonth(iso: string): string {
  return new Intl.DateTimeFormat("en", { month: "short" }).format(new Date(iso))
}

// ---------------------------------------------------------------------------
// EventRow
// ---------------------------------------------------------------------------

interface EventRowProps {
  event: StudioEventRow
  onEdit: (event: StudioEventRow) => void
}

function EventRow({ event, onEdit }: EventRowProps) {
  const [hovered, setHovered] = useState(false)
  const recLabel = recurrenceLabel(event.recurrence)

  return (
    <div
      className="flex cursor-pointer items-start gap-4 rounded-lg px-3 py-3 transition-colors hover:bg-muted/50"
      onClick={() => onEdit(event)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Date block */}
      <div className="flex w-12 shrink-0 flex-col items-center rounded-md border bg-background py-1 shadow-sm">
        <span className="text-xl font-semibold leading-none">{dayNumber(event.starts_at)}</span>
        <span className="mt-0.5 text-[10px] uppercase text-muted-foreground">
          {shortMonth(event.starts_at)}
        </span>
      </div>

      {/* Main info */}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-medium">{event.name}</span>
          <Badge
            variant={statusBadgeVariant(event.status)}
            className="text-[10px] capitalize"
          >
            {event.status.replace("_", " ")}
          </Badge>
          {recLabel && (
            <Badge variant="outline" className="text-[10px]">
              {recLabel}
            </Badge>
          )}
        </div>

        {event.description && (
          <p className="mt-0.5 line-clamp-1 text-sm text-muted-foreground">
            {event.description}
          </p>
        )}

        {event.poi_name && (
          <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin className="size-3 shrink-0" />
            <span>{event.poi_name}</span>
          </div>
        )}
      </div>

      {/* Edit button (revealed on hover or keyboard focus) */}
      <div
        className={`shrink-0 transition-opacity focus-within:opacity-100 ${hovered ? "opacity-100" : "opacity-0"}`}
      >
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2"
          aria-label={`Edit ${event.name}`}
          onClick={(e) => {
            e.stopPropagation()
            onEdit(event)
          }}
        >
          <Pencil className="size-3.5" />
        </Button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// EventsTab
// ---------------------------------------------------------------------------

export function EventsTab({
  events,
  pois,
  urls,
  editingEvent,
  onEditingEventChange,
}: EventsTabProps) {
  const [scope, setScope] = useState<ScopeFilter>("upcoming")

  const now = useMemo(() => new Date(), [])

  const { upcoming, past } = useMemo(() => {
    const up: StudioEventRow[] = []
    const pa: StudioEventRow[] = []
    for (const ev of events.records) {
      if (new Date(ev.starts_at) > now) {
        up.push(ev)
      } else {
        pa.push(ev)
      }
    }
    return { upcoming: up, past: pa }
  }, [events.records, now])

  const filtered = scope === "upcoming" ? upcoming : past

  // Group by month
  const grouped = useMemo(() => {
    const map = new Map<string, StudioEventRow[]>()
    for (const ev of filtered) {
      const key = monthKey(ev.starts_at)
      const existing = map.get(key)
      if (existing) {
        existing.push(ev)
      } else {
        map.set(key, [ev])
      }
    }
    return map
  }, [filtered])

  // Sheet state
  const sheetOpen = editingEvent !== null
  const sheetEvent = editingEvent === "new" ? null : editingEvent

  return (
    <div className="space-y-4">
      {/* Scope toggle */}
      <div className="flex items-center gap-1.5">
        <Button
          variant={scope === "upcoming" ? "default" : "outline"}
          size="sm"
          className="h-7 px-2.5 text-xs"
          onClick={() => setScope("upcoming")}
        >
          Upcoming
          <span className="ml-1 opacity-60">{upcoming.length}</span>
        </Button>
        <Button
          variant={scope === "past" ? "default" : "outline"}
          size="sm"
          className="h-7 px-2.5 text-xs"
          onClick={() => setScope("past")}
        >
          Past
          <span className="ml-1 opacity-60">{past.length}</span>
        </Button>
      </div>

      {/* Content */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16">
          <Calendar className="mb-2 size-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            No {scope} events
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {Array.from(grouped.entries()).map(([month, monthEvents]) => (
            <div key={month}>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {month}
              </h3>
              <div className="divide-y rounded-lg border bg-card">
                {monthEvents.map((ev) => (
                  <EventRow key={ev.id} event={ev} onEdit={onEditingEventChange} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Event Sheet — keyed so useForm re-initializes when the selected event changes */}
      <EventSheet
        key={sheetEvent ? sheetEvent.id : "new"}
        open={sheetOpen}
        onOpenChange={(open) => {
          if (!open) onEditingEventChange(null)
        }}
        event={sheetEvent}
        pois={pois.records}
        urls={urls}
      />
    </div>
  )
}
